if (!customElements.get("installment-rotator")) {
 class InstallmentRotator extends HTMLElement {
  connectedCallback() {
   if (this.initialized) {
    if (!this.scrollable) this.startRotation();
    return;
   }

   this.scrollable = this.hasAttribute("data-scrollable");
   this.track = this.scrollable
    ? this.querySelector(":scope > .installment-rotator__track")
    : this.querySelector(":scope > .installment-rotator__track") || this;
   this.items = Array.from(
    this.track?.querySelectorAll(":scope > .installemnt-payment") || [],
   );
   if (!this.track || !this.items.length) return;

   this.initialized = true;
   this.activeIndex = 0;
   if (this.items.length < 2) {
    this.items[0].hidden = false;
    this.items[0].setAttribute("aria-hidden", "false");
    return;
   }

   this.createDots();

   if (this.scrollable) {
    this.setupScrollableTrack();
   } else {
    this.setupAutoRotation();
   }
   this.render();
  }

  disconnectedCallback() {
   this.stopRotation();
   if (this.scrollFrame) window.cancelAnimationFrame(this.scrollFrame);
  }

  createDots() {
   this.dots = document.createElement("div");
   this.dots.className = "installment-rotator__dots";
   this.dots.setAttribute(
    "aria-label",
    this.dataset.dotsLabel || "Installment payment options",
   );

   this.dotButtons = this.items.map((item, index) => {
    const dot = document.createElement("button");
    const optionName = item.dataset.optionName || `Option ${index + 1}`;
    const showOptionLabel =
     this.dataset.showOptionLabel || "Show installment option from";

    dot.type = "button";
    dot.className = "installment-rotator__dot";
    dot.setAttribute("aria-label", `${showOptionLabel} ${optionName}`);
    dot.addEventListener("click", () => {
     this.activeIndex = index;
     if (this.scrollable) {
      this.scrollToItem(index);
     }
     this.render();
     if (!this.scrollable) this.startRotation();
    });
    this.dots.appendChild(dot);
    return dot;
   });

   this.appendChild(this.dots);
  }

  setupScrollableTrack() {
   this.items.forEach((item) => {
    item.hidden = false;
    item.setAttribute("aria-hidden", "false");
   });

   this.onTrackScroll = () => {
    if (this.scrollFrame) window.cancelAnimationFrame(this.scrollFrame);
    this.scrollFrame = window.requestAnimationFrame(() => {
     const trackRect = this.track.getBoundingClientRect();
     const trackCenter = trackRect.left + trackRect.width / 2;
     let nearestIndex = 0;
     let nearestDistance = Infinity;

     this.items.forEach((item, index) => {
      const itemRect = item.getBoundingClientRect();
      const itemCenter = itemRect.left + itemRect.width / 2;
      const distance = Math.abs(itemCenter - trackCenter);

      if (distance < nearestDistance) {
       nearestDistance = distance;
       nearestIndex = index;
      }
     });

     if (nearestIndex !== this.activeIndex) {
      this.activeIndex = nearestIndex;
      this.render();
     }
    });
   };

   this.track.addEventListener("scroll", this.onTrackScroll, {
    passive: true,
   });
  }

  setupAutoRotation() {
   this.addEventListener("mouseenter", () => this.stopRotation());
   this.addEventListener("mouseleave", () => this.startRotation());
   this.addEventListener("focusin", () => this.stopRotation());
   this.addEventListener("focusout", () => this.startRotation());
   this.startRotation();
  }

  prefersReducedMotion() {
   return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  }

  startRotation() {
   if (this.scrollable || this.items.length < 2 || this.prefersReducedMotion()) {
    return;
   }
   this.stopRotation();
   this.interval = window.setInterval(() => {
    this.activeIndex = (this.activeIndex + 1) % this.items.length;
    this.render();
   }, 6000);
  }

  stopRotation() {
   if (!this.interval) return;
   window.clearInterval(this.interval);
   this.interval = null;
  }

  scrollToItem(index) {
   const item = this.items[index];
   if (!item) return;

   const trackRect = this.track.getBoundingClientRect();
   const itemRect = item.getBoundingClientRect();
   const isRtl = window.getComputedStyle(this.track).direction === "rtl";
   const offset = isRtl
    ? itemRect.right - trackRect.right
    : itemRect.left - trackRect.left;

   this.track.scrollBy({
    left: offset,
    behavior: this.prefersReducedMotion()
     ? "auto"
     : "smooth",
   });
  }

  render() {
   if (!this.scrollable) {
    this.items.forEach((item, index) => {
     const isActive = index === this.activeIndex;
     item.hidden = !isActive;
     item.setAttribute("aria-hidden", String(!isActive));
    });
   }

   this.dotButtons?.forEach((dot, index) => {
    dot.setAttribute("aria-current", String(index === this.activeIndex));
   });
  }
 }

 customElements.define("installment-rotator", InstallmentRotator);
}
